import React, { useState } from 'react';
import { testAllConnections, testSupabase, testPinata, testReown } from '../utils/testConnections';
import { FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ConnectionTest = () => {
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);

  const runTests = async () => {
    setTesting(true);
    setResults(null);
    
    try {
      const testResults = await testAllConnections();
      setResults(testResults);
    } catch (error) {
      console.error('Test error:', error);
      setResults({
        supabase: { success: false, error: error.message },
        pinata: { success: false, error: error.message },
        reown: { success: false, error: error.message },
      });
    } finally {
      setTesting(false);
    }
  };

  const testIndividual = async (service) => {
    setTesting(true);
    try {
      let result;
      switch (service) {
        case 'supabase':
          result = await testSupabase();
          break;
        case 'pinata':
          result = await testPinata();
          break;
        case 'reown':
          result = testReown();
          break;
        default:
          return;
      }
      setResults(prev => ({ ...prev, [service]: result }));
    } catch (error) {
      setResults(prev => ({
        ...prev,
        [service]: { success: false, error: error.message }
      }));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="glass-dark p-6 rounded-xl border border-dark-700">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-lime-bright">Connection Tests</h2>
          <p className="text-dark-300 text-sm mt-1">
            Verify all integrations are working correctly
          </p>
        </div>
        <button
          onClick={runTests}
          disabled={testing}
          className="glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm disabled:opacity-50"
        >
          {testing ? (
            <>
              <SafeIcon icon={FiLoader} className="inline-block mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            'Run All Tests'
          )}
        </button>
      </div>

      <div className="space-y-4">
        {/* Supabase Test */}
        <div className="glass p-4 rounded-lg border border-dark-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {results?.supabase ? (
                results.supabase.success ? (
                  <SafeIcon icon={FiCheckCircle} className="text-lime-500 text-xl" />
                ) : (
                  <SafeIcon icon={FiXCircle} className="text-red-500 text-xl" />
                )
              ) : (
                <div className="w-5 h-5 border-2 border-dark-500 border-t-lime-500 rounded-full animate-spin" />
              )}
              <div>
                <h3 className="font-medium text-white">Supabase</h3>
                <p className="text-sm text-dark-300">
                  {results?.supabase?.success
                    ? 'Database connection successful'
                    : results?.supabase?.error || 'Not tested'}
                </p>
              </div>
            </div>
            <button
              onClick={() => testIndividual('supabase')}
              disabled={testing}
              className="text-sm text-lime-400 hover:text-lime-300 disabled:opacity-50"
            >
              Test
            </button>
          </div>
        </div>

        {/* Pinata Test */}
        <div className="glass p-4 rounded-lg border border-dark-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {results?.pinata ? (
                results.pinata.success ? (
                  <SafeIcon icon={FiCheckCircle} className="text-lime-500 text-xl" />
                ) : (
                  <SafeIcon icon={FiXCircle} className="text-red-500 text-xl" />
                )
              ) : (
                <div className="w-5 h-5 border-2 border-dark-500 border-t-lime-500 rounded-full animate-spin" />
              )}
              <div>
                <h3 className="font-medium text-white">Pinata IPFS</h3>
                <p className="text-sm text-dark-300">
                  {results?.pinata?.success
                    ? 'IPFS service connected'
                    : results?.pinata?.error || 'Not tested'}
                </p>
              </div>
            </div>
            <button
              onClick={() => testIndividual('pinata')}
              disabled={testing}
              className="text-sm text-lime-400 hover:text-lime-300 disabled:opacity-50"
            >
              Test
            </button>
          </div>
        </div>

        {/* Reown Test */}
        <div className="glass p-4 rounded-lg border border-dark-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {results?.reown ? (
                results.reown.success ? (
                  <SafeIcon icon={FiCheckCircle} className="text-lime-500 text-xl" />
                ) : (
                  <SafeIcon icon={FiXCircle} className="text-red-500 text-xl" />
                )
              ) : (
                <div className="w-5 h-5 border-2 border-dark-500 border-t-lime-500 rounded-full animate-spin" />
              )}
              <div>
                <h3 className="font-medium text-white">Reown AppKit</h3>
                <p className="text-sm text-dark-300">
                  {results?.reown?.success
                    ? 'Wallet connection configured'
                    : results?.reown?.error || 'Not tested'}
                </p>
              </div>
            </div>
            <button
              onClick={() => testIndividual('reown')}
              disabled={testing}
              className="text-sm text-lime-400 hover:text-lime-300 disabled:opacity-50"
            >
              Test
            </button>
          </div>
        </div>
      </div>

      {results && (
        <div className="mt-6 pt-6 border-t border-dark-700">
          <div className="flex items-center space-x-2">
            {Object.values(results).every(r => r.success) ? (
              <>
                <SafeIcon icon={FiCheckCircle} className="text-lime-500 text-xl" />
                <span className="text-lime-400 font-medium">
                  All connections successful! Ready to develop.
                </span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiXCircle} className="text-red-500 text-xl" />
                <span className="text-red-400 font-medium">
                  Some connections failed. Check errors above.
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionTest;

